import * as React from "react";
import { MarketplaceReleaseInstance, ReleaseRevision } from "../types/release";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { useNavigate } from "react-router-dom";
import { Template } from "../types/template";
import { request, resourceLink } from "../../../services/cpln";
import { Alert, Button, Modal, notification, Select } from "antd";
import jsYaml from "js-yaml";
import { Loader } from "../../../components/layout/loader";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { k8sKeySort } from "../../../services/utils";
import { decodeRevisionFrom, getMarketplaceErrorMessage, getTemplateVersion, getVersions } from "../utils";
import {
  DOCS_URL,
  MARKETPLACE_BRANCH,
  MARKETPLACE_TEMPLATE_REPO,
} from "../../../envVariables";
import { MarketplaceIcon } from "../components/MarketplaceIcon";
import { FilesReducerActionType, useFilesReducer } from "../template/filesReducer";
import { CodeEditorModal } from "../../../components/modals/codeEditorModal";
import { ViewModal } from "../../../components/modals/viewModal";
import { isEqual } from "lodash";
import { CodeSnippet } from "../../../components/generic/codeSnippet/codeSnippet";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { addNotification } from "../../../store/notificationDrawerSlice";

interface Props {
  template: Template;
  release: MarketplaceReleaseInstance;
  currentRevisionSecretName: string;
  onRefresh?: () => Promise<void>;
}
export const ReleaseUpgrade: React.FC<Props> = ({ template, release, currentRevisionSecretName, onRefresh }) => {
  const org = useAppSelector(selectOrg);
  const dispatch = useAppDispatch();

  const basePath = React.useContext(BasePathContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);
  const [isLoadingUpgrade, setIsLoadingUpgrade] = React.useState(false);

  const [revision, setRevision] = React.useState<ReleaseRevision>(null as any);
  const versions = getVersions(template);

  // default means the default in the template version
  const [currentVersionDefaultValues, setCurrentVersionDefaultValues] = React.useState("");
  // initial means the values that user put in for the applied release
  const [initialValues, setInitialValues] = React.useState("");
  // upgrade version means what the user has selected, used when version is different from the released one
  const [upgradeVersionDefaultValues, setUpgradeVersionDefaultValues] = React.useState("");
  // values means what the user is typing now
  const [values, setValues] = React.useState("");

  const [isTemplateView, setIsTemplateView] = React.useState(false);
  const [helmTemplates, setHelmTemplates] = React.useState<any[]>([]);

  const [currentVersionDefaultFilesState, currentVersionDefaultFilesDispatch] = useFilesReducer();
  const [initialFilesState, initialFilesDispatch] = useFilesReducer();

  const [upgradeVersionDefaultFilesState, upgradeVersionDefaultFilesDispatch] = useFilesReducer();
  const [filesState, filesDispatch] = useFilesReducer();

  const [codeEditor, setCodeEditor] = React.useState<{ readonly: boolean; property: string } | null>(null);
  const [upgradeVersion, setUpgradeVersion] = React.useState(release.version);

  React.useEffect(() => {
    fetchState();
  }, []);

  async function fetchState() {
    try {
      const { data } = await request({
        url: resourceLink("secret", currentRevisionSecretName) + "/-reveal",
      });
      setRevision(decodeRevisionFrom(data)!);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({ message: "Failed", description: errorMessage });
    }
  }

  React.useEffect(() => {
    processDefaults();
  }, [template, revision, upgradeVersion]);

  function processDefaults() {
    if (!revision) {
      return;
    }
    if (!versions.includes(release.version)) {
      return;
    }
    if (!versions.includes(upgradeVersion)) {
      return;
    }

    const currentVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      currentVersionFileDefaults[property] = value.content;
    }

    currentVersionDefaultFilesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: currentVersionFileDefaults },
    });

    const values = template.versions[release.version].values;
    setCurrentVersionDefaultValues(values);

    // upgrade defaults only if upgrade version is different
    if (release.version === upgradeVersion) {
      processValues();
      return;
    }

    const upgradeVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[upgradeVersion].files || {})) {
      upgradeVersionFileDefaults[property] = value.content;
    }

    upgradeVersionDefaultFilesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: upgradeVersionFileDefaults },
    });

    const upgradeValues = template.versions[upgradeVersion].values;
    setUpgradeVersionDefaultValues(upgradeValues);

    //
    processValues();
  }

  function processValues() {
    // set initial for current version and the state depending on the different of current version and upgrade version
    const currentTemplateVersion = getTemplateVersion(template, release.version);
    const currentValuesObj: any = jsYaml.load(revision.valuesFiles[0] || currentTemplateVersion?.values || "");
    const currentValuesObjWithoutFiles: any = jsYaml.load(
      revision.valuesFiles[0] || currentTemplateVersion?.values || "",
    );

    // files
    const currentVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      currentVersionFileDefaults[property] = value.content;
    }

    const currentVersionFileInitials: { [_: string]: string } = {};
    for (const [property] of Object.entries(template.versions[release.version].files || {})) {
      const fileValueObj = currentValuesObj[property];
      const fileValueString = jsYaml.dump(fileValueObj);

      let content = fileValueString || currentVersionFileDefaults[property] || "";

      if (content.trimStart().startsWith("|-")) {
        // remove initial "|- "" and remove indent that was added manually
        content = content
          .slice(3)
          .split("\n")
          .map((l) => l.slice(2))
          .join("\n");
      }

      currentVersionFileInitials[property] = content;

      initialFilesDispatch({
        type: FilesReducerActionType.SET_FILE,
        payload: { content: content, property: property },
      });
      if (release.version === upgradeVersion) {
        filesDispatch({
          type: FilesReducerActionType.SET_FILE,
          payload: { content: content, property: property },
        });
      }
      delete currentValuesObjWithoutFiles[property];
    }

    initialFilesDispatch({
      type: FilesReducerActionType.SET_INITIALS,
      payload: { content: "", property: "", defaults: currentVersionFileInitials },
    });

    const valuesObjWithoutFilesString = jsYaml.dump(currentValuesObjWithoutFiles);

    setInitialValues(valuesObjWithoutFilesString);

    if (release.version === upgradeVersion) {
      setValues(valuesObjWithoutFilesString);
      filesDispatch({
        type: FilesReducerActionType.SET_INITIALS,
        payload: { content: "", property: "", defaults: currentVersionFileInitials },
      });
    } else {
      // set current values as the upgrade version values minus files, also set filesDispatch

      const upgradeTemplateVersion = getTemplateVersion(template, upgradeVersion);
      const upgradeValuesObj: any = jsYaml.load(upgradeTemplateVersion?.values || "");
      const upgradeValuesObjWithoutFiles: any = jsYaml.load(upgradeTemplateVersion?.values || "");

      // files
      const upgradeVersionFileInitials: { [_: string]: string } = {};
      for (const [property] of Object.entries(template.versions[upgradeVersion].files || {})) {
        const fileValueObj = upgradeValuesObj[property];
        const fileValueString = jsYaml.dump(fileValueObj);

        let content = fileValueString || "";

        if (content.trimStart().startsWith("|-")) {
          // remove initial "|- "" and remove indent that was added manually
          content = content
            .slice(3)
            .split("\n")
            .map((l) => l.slice(2))
            .join("\n");
        }

        upgradeVersionFileInitials[property] = content;

        filesDispatch({
          type: FilesReducerActionType.SET_FILE,
          payload: { content: content, property: property },
        });
        if (upgradeVersion === upgradeVersion) {
          filesDispatch({
            type: FilesReducerActionType.SET_FILE,
            payload: { content: content, property: property },
          });
        }
        delete upgradeValuesObjWithoutFiles[property];
      }

      filesDispatch({
        type: FilesReducerActionType.SET_INITIALS,
        payload: { content: "", property: "", defaults: upgradeVersionFileInitials },
      });

      const valuesObjWithoutFilesString = jsYaml.dump(upgradeValuesObjWithoutFiles);

      setValues(valuesObjWithoutFilesString);
    }
  }

  function getProcessedValues(): string {
    let processedValues = values;
    const templateVersion = getTemplateVersion(template, upgradeVersion);
    if (!templateVersion) {
      return processedValues;
    }
    if (templateVersion.files && Object.keys(templateVersion.files).length > 0) {
      for (let fileProperty of Object.keys(templateVersion.files)) {
        // TODO for handling multi files
        // const fileInfo = templateVersion.chart.files[fileProperty];
        const fileValue = filesState.current[fileProperty] || "";

        const indentCount = 2;
        const indentedFileValue = fileValue
          .split("\n")
          .map((line) => " ".repeat(indentCount) + line)
          .join("\n");

        processedValues += "\n" + fileProperty + ": |- \n" + indentedFileValue;
      }
    }
    return processedValues;
  }

  function handleUpgrade() {
    const gvcDisplay = release.gvc && release.gvc !== " " ? release.gvc : null;
    Modal.confirm({
      title: "Confirm Upgrade",
      content: (
        <div className="flex flex-col gap-2">
          <div>Are you sure you want to upgrade this release?</div>
          {gvcDisplay && (
            <div>
              <span className="font-medium">GVC:</span>{" "}
              <span className="font-bold" style={{ color: "var(--color-primary)" }}>
                {gvcDisplay}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium">Release Name:</span>{" "}
            <span className="font-bold" style={{ color: "var(--color-primary)" }}>
              {release.name}
            </span>
          </div>
          <div>
            <span className="font-medium">Version:</span>{" "}
            <span className="font-bold">
              {release.version} → {upgradeVersion}
            </span>
          </div>
        </div>
      ),
      okText: "Upgrade",
      okButtonProps: { type: "primary", className: "action" },
      cancelText: "Cancel",
      onOk: async () => {
        await performUpgrade();
      },
    });
  }

  async function performUpgrade() {
    try {
      setIsLoadingUpgrade(true);
      await request({
        service: "marketplace",
        method: "post",
        url: `/helm/install`,
        body: {
          org: release.org,
          gvc: release.gvc === " " ? "" : release.gvc,
          name: release.name,
          template: release.template,
          version: upgradeVersion,
          values: getProcessedValues(),
          action: "upgrade",
        },
      });
      notification.success({ message: "Success", description: "Upgraded template" });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Upgrade",
        success: true,
        message: `Upgraded release: ${release.name} to version ${upgradeVersion}`,
      }));
      setIsLoadingUpgrade(false);
      if (onRefresh) {
        await onRefresh();
      }
      navigate(basePath);
    } catch (e) {
      setIsLoadingUpgrade(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Upgrade",
        success: false,
        message: `Failed to upgrade release: ${release.name}`,
        details: errorMessage.join("\n"),
      }));
    }
  }

  async function handleTemplate() {
    try {
      setIsLoadingTemplate(true);
      const { data } = await request({
        service: "marketplace",
        method: "post",
        url: `/helm/template`,
        body: {
          org: org,
          gvc: release.gvc === " " ? "" : release.gvc,
          name: release.name,
          template: template.name,
          version: upgradeVersion,
          values: getProcessedValues(),
        },
      });
      setHelmTemplates(jsYaml.loadAll(data.message));
      setIsTemplateView(true);
      notification.success({ message: "Success", description: "Templated" });
      setIsLoadingTemplate(false);
    } catch (e) {
      setIsLoadingTemplate(false);
      setHelmTemplates([]);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Template",
        success: false,
        message: `Failed to template: ${release.name}`,
        details: errorMessage.join("\n"),
      }));
    }
  }

  if (isLoading) {
    return (
      <div className="relative">
        <Loader
          style={{
            width: 100,
            height: 100,
            margin: 0,
            left: `calc(50% - 50px)`,
            right: "unset",
            top: 150,
            bottom: "unset",
          }}
        />
      </div>
    );
  }

  let filesAreDirty = false;
  if (release.version !== upgradeVersion) {
    filesAreDirty = !isEqual(filesState.current, upgradeVersionDefaultFilesState.initials);
  } else {
    filesAreDirty = !isEqual(filesState.current, initialFilesState.initials);
  }

  const placeholder = [""];

  return (
    <>
      <div style={{ maxWidth: 900 }}>
        <div className="header-inputs">
          <div
            className="flex mb-4 px-4 py-1 rounded w-fit"
            style={{ backgroundColor: "white", color: "var(--color-gray-1600)" }}
          >
            {template.hasIcon !== false && (
              <div style={{ width: 50 }} className="flex items-center">
                <MarketplaceIcon templateName={template.name} hasIcon={template.hasIcon} className="object-fit" />
              </div>
            )}
            <div className="flex flex-col ml-4">
              <span className="leading-none text-lg font-semibold">{template.name}</span>

              <span className="leading-none text-sm">{getTemplateVersion(template, release.version)?.category}</span>
            </div>
          </div>
          <div className="grid grid-cols-4">
            {release.gvc !== " " ? (
              <div>
                <FormLabel>GVC</FormLabel>
                <div className="mb-4">{release.gvc}</div>
              </div>
            ) : null}
            <div>
              <FormLabel>Release Name</FormLabel>
              <div className="mb-4">{release.name}</div>
            </div>
            <div>
              <FormLabel>Released Version</FormLabel>
              <div className="mb-4">{release.version}</div>
            </div>
            <div>
              <FormLabel>Upgrade Version</FormLabel>
              {Object.keys(template.versions).length > 0 ? (
                <Select
                  size={`small`}
                  value={upgradeVersion}
                  onChange={setUpgradeVersion}
                  options={Object.entries(template.versions).map(([key, value]) => ({ label: key, value: key }))}
                />
              ) : (
                <div className="mb-4">{release.version}</div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="mb-8 flex gap-4">
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Initial</div>
            <div>Values</div>
            <div>{initialValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(initialFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(initialFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(initialFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Current</div>
            <div>Defaults</div>
            <div>{currentVersionDefaultValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(currentVersionDefaultFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(currentVersionDefaultFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(currentVersionDefaultFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Upgrade</div>
            <div>Defaults</div>
            <div>{upgradeVersionDefaultValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(upgradeVersionDefaultFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(upgradeVersionDefaultFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(upgradeVersionDefaultFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Actual</div>
            <div>Values</div>
            <div>{values}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(filesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(filesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(filesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
        </div> */}

        {release.version === upgradeVersion ? (
          <>
            <FormLabel>Values</FormLabel>
            <div className="text-sm">
              <CodeEditor language="yaml" options={{}} value={values} setValue={setValues} />
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2 items-start text-sm">
              <div className="flex-grow">
                <FormLabel>Current Values</FormLabel>
              </div>
              <div className="flex-grow">
                <FormLabel>New Values</FormLabel>
              </div>
            </div>
            <div className="flex gap-2 items-start text-sm">
              <CodeEditor
                language="yaml"
                options={{ readOnly: true, style: { flexBasis: 1, flexGrow: 1 } }}
                value={initialValues}
                setValue={() => {}}
              />
              <CodeEditor
                language="yaml"
                options={{ style: { flexBasis: 1, flexGrow: 1 } }}
                value={values}
                setValue={setValues}
              />
            </div>
          </>
        )}

        <>
          {release.version === upgradeVersion
            ? placeholder.map((_) => {
                const templateVersion = getTemplateVersion(template, release.version);
                if (!templateVersion) return null;
                return (
                  <div key={`same`} className="mt-4">
                    {Object.keys(templateVersion.files || {}).length < 1 ? null : (
                      <>
                        {Object.entries(templateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                          if (fileInfo.type === "multi") {
                            return null;
                          }

                          const content = filesState.current[fileProperty];

                          return (
                            <div className="my-4">
                              {content ? (
                                <div>
                                  <FormLabel>{fileInfo.label}</FormLabel>
                                  <Button
                                    type={"default"}
                                    onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              ) : (
                                <div>
                                  <FormLabel>{fileInfo.label}</FormLabel>
                                  <Button
                                    className="mt-2"
                                    type="primary"
                                    // TODO make this more clear, let it know which value to update
                                    onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                  >
                                    Set Data
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })
            : null}
          {release.version !== upgradeVersion
            ? placeholder.map((_) => {
                const currentTemplateVersion = getTemplateVersion(template, release.version);
                if (!currentTemplateVersion) return null;

                const upgradeTemplateVersion = getTemplateVersion(template, upgradeVersion);
                if (!upgradeTemplateVersion) return null;

                const currentFileCount = Object.keys(currentTemplateVersion.files || {}).length;
                const upgradeFileCount = Object.keys(upgradeTemplateVersion.files || {}).length;

                return (
                  <div key={`different`} className="mt-4">
                    {currentFileCount < 1 && upgradeFileCount < 1 ? null : (
                      <div className="flex items-center gap-2">
                        <div className={`${currentFileCount < 1 ? "invisible" : ""} flex-grow`}>
                          <FormLabel>Old Files</FormLabel>
                        </div>
                        <div className={`${upgradeFileCount < 1 ? "invisible" : ""} flex-grow`}>
                          <FormLabel>New Files</FormLabel>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center">
                      {Object.keys(currentTemplateVersion.files || {}).length < 1 ? null : (
                        <div className="flex-grow">
                          {Object.entries(currentTemplateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                            if (fileInfo.type === "multi") {
                              return null;
                            }

                            const content = initialFilesState.initials[fileProperty];

                            return (
                              <div className="my-4">
                                <FormLabel>{fileInfo.label}</FormLabel>
                                {/* // TODO make this modal only show the data without editing */}
                                <Button
                                  type={"default"}
                                  onClick={() => setCodeEditor({ readonly: true, property: fileProperty })}
                                >
                                  View
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {Object.keys(upgradeTemplateVersion.files || {}).length < 1 ? null : (
                        <div className="flex-grow">
                          {Object.entries(upgradeTemplateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                            if (fileInfo.type === "multi") {
                              return null;
                            }

                            const content = filesState.current[fileProperty];

                            return (
                              <div className="my-4">
                                {content ? (
                                  <div>
                                    <FormLabel>{fileInfo.label}</FormLabel>
                                    <Button
                                      type={"default"}
                                      onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                    >
                                      Edit
                                    </Button>
                                  </div>
                                ) : (
                                  <div>
                                    <FormLabel>{fileInfo.label}</FormLabel>
                                    <Button
                                      className="mt-2"
                                      type="primary"
                                      // TODO make this more clear, let it know which value to update
                                      onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                    >
                                      Set Data
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            : null}
        </>
        {release.version !== upgradeVersion ? (
          <Alert
            type="warning"
            message="IMPORTANT: Before upgrading to a new version, test the upgrade in a non production environment to ensure correctness. Without proper testing you risk data loss."
          />
        ) : null}
        <div className="mt-4 flex items-center">
          <Button
            style={{ width: 220, marginRight: 10 }}
            disabled={(!filesAreDirty && values === initialValues) || isLoadingUpgrade || isLoadingTemplate}
            type={"default"}
            danger
            onClick={() => {
              if (release.version !== upgradeVersion) {
                setUpgradeVersion(release.version);
              } else {
                processDefaults();
              }
            }}
          >
            Reset
          </Button>
          <Button
            style={{ width: 220, marginRight: 10 }}
            // TODO we can have a way of validating the values
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingTemplate}
            type={"primary"}
            onClick={() => handleTemplate()}
          >
            Template
          </Button>
          <Button
            style={{ width: 220 }}
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingUpgrade}
            type={"primary"}
            className={"action"}
            onClick={() => handleUpgrade()}
          >
            Upgrade App
          </Button>
        </div>
      </div>
      <div className={`mt-4 p-4 border`}>
        <p>
          Install via terminal using the{" "}
          <a href={`${DOCS_URL}/guides/cpln-helm#upgrade-a-release`} target="_blank" className="color-link">
            Helm Upgrade
          </a>{" "}
          CLI command, after{" "}
          <a
            href={`${MARKETPLACE_TEMPLATE_REPO}/${MARKETPLACE_BRANCH}/${release.template}/versions/${upgradeVersion}`}
            target="_blank"
            className="color-link"
          >
            downloading the template repo
          </a>
          , navigating to the template and updating the <span className="font-bold">values.yaml</span> file.
        </p>
        <CodeSnippet
          code={`cpln helm upgrade ${
            release.name || "{name}"
          } -f values.yaml --tag cpln/marketplace=true --tag cpln/marketplace-gvc=${
            release.gvc || "{gvc}"
          } --tag cpln/marketplace-template=${
            release.template
          } --tag cpln/marketplace-template-version=${upgradeVersion} --org ${org} --gvc ${release.gvc || "{gvc}"}`}
          className="mt-3 text-sm"
        />
      </div>
      {codeEditor === null ? null : (
        <CodeEditorModal
          title={getTemplateVersion(template, upgradeVersion)?.files?.[codeEditor.property]?.label || "File"}
          initialValue={
            codeEditor.readonly
              ? initialFilesState.initials[codeEditor.property]
              : filesState.current[codeEditor.property] || ""
          }
          open={codeEditor !== null}
          readonly={codeEditor.readonly}
          onOk={(value) => {
            filesDispatch({
              type: FilesReducerActionType.SET_FILE,
              payload: { content: value, property: codeEditor.property },
            });
            setCodeEditor(null);
          }}
          onClose={() => setCodeEditor(null)}
        />
      )}
      <ViewModal
        visible={isTemplateView}
        onClose={() => setIsTemplateView(false)}
        closable={!isLoadingUpgrade}
        title={`Template Preview - ${helmTemplates.length} Items`}
        filename={`marketplace-${template.name}-items`}
        rawContent={jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort })}
        rawLanguage="yaml"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsTemplateView(false)} disabled={isLoadingUpgrade}>
              Cancel
            </Button>
            <Button
              type="primary"
              className="action"
              onClick={performUpgrade}
              loading={isLoadingUpgrade}
              disabled={isLoadingUpgrade}
            >
              Upgrade
            </Button>
          </div>
        }
      />
    </>
  );
};
